import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  GitCompare,
  Play,
  Radio,
  RefreshCw,
  RotateCcw,
  Search,
  Shield,
  X,
  Zap,
} from 'lucide-react';
import {
  ACTIVE_ALERTS,
  ALERT_RULES,
  CHART_DATA,
  COST_DATA,
  COST_ROWS,
  DEPT_ANALYTICS,
  ERROR_ROWS,
  EVALUATION_SCORES,
  LATENCY_DATA,
  RAG_CHUNKS,
  TRACE_ROWS,
  TRACE_SPANS,
  WORKFLOW_NODES,
} from './data';
import type {
  ChartTab,
  DateRange,
  DrawerTab,
  Environment,
  TraceRow,
  TraceSpan,
} from './types';
import { useTheme } from '../../../components/common/Theme/ThemeContext';
import { ObsThemeProvider, useObsTheme } from './theme';
import LlmFlowSimulationModal from './LlmFlowSimulationModal';
import LlmInspectorCallList from './LlmInspectorCallList';
import { LLM_INSPECTOR_CALLS, type LlmInspectorCall } from './llmInspectorData';
import {
  Card,
  CardHeader,
  CompactTable,
  EmptyState,
  FieldRow,
  KpiCard,
  ProviderBadge,
  RiskBadge,
  SectionLabel,
  SelectField,
  SkeletonRow,
  SpanStatusDot,
  SpanStatusText,
  StatusBadge,
  TabBar,
} from './ui';

const CHART_TABS: { id: ChartTab; label: string }[] = [
  { id: 'requests', label: 'Requests' },
  { id: 'latency', label: 'Latency' },
  { id: 'errors', label: 'Errors' },
  { id: 'cost', label: 'Cost' },
  { id: 'tokens', label: 'Tokens' },
];

const DRAWER_TABS: { id: DrawerTab; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'metadata', label: 'Metadata' },
  { id: 'input', label: 'Input' },
  { id: 'output', label: 'Output' },
  { id: 'logs', label: 'Logs' },
  { id: 'policy', label: 'Policy' },
  { id: 'cost', label: 'Cost' },
  { id: 'errors', label: 'Errors' },
  { id: 'actions', label: 'Actions' },
];

const COST_BY_MODEL = [
  { name: 'GPT-4o', value: 482, color: '#6366f1' },
  { name: 'Claude 3.5', value: 286, color: '#f59e0b' },
  { name: 'Mistral Large', value: 186, color: '#8b5cf6' },
  { name: 'GPT-4o-mini', value: 124, color: '#3b82f6' },
  { name: 'Local Llama', value: 68, color: '#10b981' },
];

function SpanTree({
  spans,
  expanded,
  onToggle,
  selectedId,
  onSelect,
}: {
  spans: TraceSpan[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selectedId: string | null;
  onSelect: (span: TraceSpan) => void;
}) {
  const t = useObsTheme();
  return (
    <>
      {spans.map((span) => {
        const hasChildren = span.children && span.children.length > 0;
        const isExpanded = expanded.has(span.id);
        const isSelected = selectedId === span.id;
        return (
          <React.Fragment key={span.id}>
            <tr
              className={`cursor-pointer transition-colors ${t.rowHover} ${isSelected ? t.rowSelected : ''}`}
              onClick={() => onSelect(span)}
            >
              <td className="px-3 py-1.5">
                <div className="flex items-center gap-1" style={{ paddingLeft: span.depth * 16 }}>
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle(span.id);
                      }}
                      className={`p-0.5 ${t.iconMuted} ${t.iconHover}`}
                    >
                      {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </button>
                  ) : (
                    <span className="w-4" />
                  )}
                  <SpanStatusDot status={span.status} />
                  <span className={`text-[11px] font-medium ${t.text}`}>{span.name}</span>
                </div>
              </td>
              <td className={`px-3 py-1.5 text-[10px] font-mono ${t.muted}`}>{span.startTime}</td>
              <td className={`px-3 py-1.5 text-[10px] font-mono ${t.text}`}>{span.duration}</td>
              <td className="px-3 py-1.5"><SpanStatusText status={span.status} /></td>
              <td className={`px-3 py-1.5 text-[10px] ${t.muted}`}>{span.inputSize}</td>
              <td className={`px-3 py-1.5 text-[10px] ${t.muted}`}>{span.outputSize}</td>
              <td className={`px-3 py-1.5 text-[10px] font-mono ${t.text}`}>{span.cost}</td>
              <td className={`px-3 py-1.5 text-[10px] tabular-nums ${t.textSecondary}`}>{span.tokens || '—'}</td>
              <td className={`px-3 py-1.5 text-[10px] ${t.muted}`}>{span.retries || '—'}</td>
              <td className={`px-3 py-1.5 text-[10px] ${t.muted} max-w-[120px] truncate`}>
                {span.policyDecision || span.error || '—'}
              </td>
            </tr>
            {hasChildren && isExpanded && (
              <SpanTree
                spans={span.children!}
                expanded={expanded}
                onToggle={onToggle}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

function TraceDrawer({
  trace,
  selectedSpan,
  onClose,
}: {
  trace: TraceRow;
  selectedSpan: TraceSpan | null;
  onClose: () => void;
}) {
  const t = useObsTheme();
  const [tab, setTab] = useState<DrawerTab>('summary');
  const target = selectedSpan?.name ?? trace.agentWorkflow;

  return (
    <div className={`fixed inset-y-0 right-0 w-[380px] border-l shadow-xl z-50 flex flex-col ${t.drawer}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${t.drawerHeader}`}>
        <div>
          <p className={`text-[10px] font-medium ${t.muted} uppercase`}>Trace Inspection</p>
          <p className={`text-xs font-semibold ${t.title} font-mono truncate max-w-[280px]`}>{trace.id}</p>
        </div>
        <button type="button" onClick={onClose} className={`p-1 rounded ${t.iconMuted} ${t.iconHover}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
      <TabBar tabs={DRAWER_TABS} active={tab} onChange={setTab} />
      <div className="flex-1 overflow-y-auto p-4 text-[11px]">
        {tab === 'summary' && (
          <div className="space-y-1">
            <FieldRow label="Target" value={target} />
            <FieldRow label="Status" value={<StatusBadge status={trace.status} />} />
            <FieldRow label="Risk" value={<RiskBadge risk={trace.risk} />} />
            <FieldRow label="Duration" value={trace.duration} />
            <FieldRow label="Cost" value={trace.cost} mono />
            <FieldRow label="Tokens" value={trace.tokens.toLocaleString()} />
            <FieldRow label="Policy" value={trace.policy} />
            <FieldRow label="Model" value={trace.model} />
            <FieldRow label="User" value={trace.user} />
          </div>
        )}
        {tab === 'metadata' && (
          <div className="space-y-1 font-mono text-[10px]">
            {[
              ['trace_id', trace.id],
              ['run_id', 'run-' + trace.id.slice(3, 11)],
              ['node_run_id', selectedSpan?.id ?? '—'],
              ['tenant_id', 'tenant-acme-prod'],
              ['user_id', trace.user],
              ['agent_id', 'agent-' + trace.agent.toLowerCase().replace(/\s/g, '-')],
              ['workflow_id', trace.workflow === '—' ? '—' : 'wf-credit-risk'],
              ['model', trace.model],
              ['provider', trace.provider],
              ['policy_id', 'pol-financial-strict'],
              ['policy_version', '2.4.1'],
              ['correlation_id', 'corr-8f2a91c4'],
              ['request_id', 'req-e3b7a9f4'],
            ].map(([k, v]) => (
              <FieldRow key={k} label={k} value={v} mono />
            ))}
          </div>
        )}
        {tab === 'input' && (
          <pre className={`text-[10px] font-mono border rounded p-3 whitespace-pre-wrap ${t.pre}`}>
            {`{\n  "prompt": "Analyze Q3 revenue trends and compare to forecast...",\n  "context": {\n    "department": "${trace.department}",\n    "intent": "${trace.intent}"\n  }\n}`}
          </pre>
        )}
        {tab === 'output' && (
          <pre className={`text-[10px] font-mono border rounded p-3 whitespace-pre-wrap ${t.pre}`}>
            Q3 revenue increased 12.4% YoY, exceeding forecast by 2.1%. Primary drivers: enterprise segment (+18%) and APAC expansion (+24%). [PII masked per policy]
          </pre>
        )}
        {tab === 'logs' && (
          <div className="space-y-2 font-mono text-[10px]">
            <p className={t.muted}>10:42:18.002 [INFO] Trace started</p>
            <p className={t.muted}>10:42:18.058 [INFO] Prompt firewall: ALLOW</p>
            <p className={t.muted}>10:42:18.318 [INFO] RAG retrieval: 5 chunks</p>
            <p className={t.muted}>10:42:18.812 [INFO] LLM call initiated</p>
            <p className="text-emerald-500">10:42:20.414 [INFO] Trace completed successfully</p>
          </div>
        )}
        {tab === 'policy' && (
          <div className="space-y-2">
            <FieldRow label="Policy" value={trace.policy} />
            <FieldRow label="Version" value="2.4.1" />
            <FieldRow label="Source" value="Department Default" />
            <FieldRow label="Prompt" value="Allowed" />
            <FieldRow label="KB" value="Allowed (2 masked)" />
            <FieldRow label="MCP Tool" value="Allowed" />
            <FieldRow label="Output" value="PII Masked" />
          </div>
        )}
        {tab === 'cost' && (
          <div className="space-y-1">
            <FieldRow label="LLM" value="$0.068" mono />
            <FieldRow label="RAG" value="$0.008" mono />
            <FieldRow label="Firewall" value="$0.002" mono />
            <FieldRow label="MCP" value="$0.002" mono />
            <FieldRow label="Total" value={trace.cost} mono />
          </div>
        )}
        {tab === 'errors' && (
          <EmptyState title="No errors" description="This trace completed without errors." />
        )}
        {tab === 'actions' && (
          <div className="space-y-2">
            {['Replay trace', 'Compare with previous run', 'Export trace JSON', 'View raw payload', 'Open workflow run', 'Open audit log'].map((a) => (
              <button
                key={a}
                type="button"
                className={`w-full text-left px-3 py-2 text-[11px] font-medium border rounded-md flex items-center gap-2 ${t.btnSecondary} ${t.surfaceHover}`}
              >
                <Play className={`w-3 h-3 ${t.link}`} />
                {a}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ObservabilityDashboard() {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  return (
    <ObsThemeProvider isLight={isLight}>
      <ObservabilityDashboardContent />
    </ObsThemeProvider>
  );
}

function ObservabilityDashboardContent() {
  const t = useObsTheme();
  const [environment, setEnvironment] = useState<Environment>('Production');
  const [tenant, setTenant] = useState('Acme Corp');
  const [dateRange, setDateRange] = useState<DateRange>('24h');
  const [search, setSearch] = useState('');
  const [liveView, setLiveView] = useState(true);
  const [loading, setLoading] = useState(false);
  const [chartTab, setChartTab] = useState<ChartTab>('requests');
  const [selectedCall, setSelectedCall] = useState<LlmInspectorCall | null>(null);
  const [selectedTrace, setSelectedTrace] = useState<TraceRow | null>(TRACE_ROWS[0]);
  const [selectedSpan, setSelectedSpan] = useState<TraceSpan | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedSpans, setExpandedSpans] = useState<Set<string>>(() => new Set(['span-root', 'span-agent', 'span-rag']));
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const filteredTraces = useMemo(() => {
    if (!search.trim()) return TRACE_ROWS;
    const q = search.toLowerCase();
    return TRACE_ROWS.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.user.toLowerCase().includes(q) ||
        t.agentWorkflow.toLowerCase().includes(q) ||
        t.model.toLowerCase().includes(q) ||
        t.policy.toLowerCase().includes(q)
    );
  }, [search]);

  const toggleSpan = useCallback((id: string) => {
    setExpandedSpans((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectTrace = (trace: TraceRow) => {
    setSelectedTrace(trace);
    setDrawerOpen(true);
    setSelectedSpan(null);
  };

  useEffect(() => {
    if (!liveView) return;
    const interval = setInterval(() => setLoading((l) => !l), 4000);
    return () => clearInterval(interval);
  }, [liveView]);

  const chartContent = useMemo(() => {
    const grid = t.chartGrid;
    const tick = { fontSize: 10, fill: t.chartTick };
    const tooltip = t.chartTooltipStyle;
    const legend = { fontSize: 10, color: t.chartTick };

    if (chartTab === 'requests') {
      return (
        <ComposedChart data={CHART_DATA}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} />
          <XAxis dataKey="time" tick={tick} />
          <YAxis tick={tick} />
          <Tooltip contentStyle={tooltip} />
          <Legend wrapperStyle={legend} />
          <Bar dataKey="llm" name="LLM calls" fill="#8b5cf6" stackId="a" radius={[0, 0, 0, 0]} />
          <Bar dataKey="workflow" name="Workflow runs" fill="#3b82f6" stackId="a" />
          <Bar dataKey="mcp" name="MCP tool calls" fill="#10b981" stackId="a" />
          <Bar dataKey="kb" name="KB retrievals" fill="#64748b" stackId="a" radius={[2, 2, 0, 0]} />
          <Line type="monotone" dataKey="failed" name="Failed" stroke="#ef4444" strokeWidth={2} dot={false} />
        </ComposedChart>
      );
    }
    if (chartTab === 'latency') {
      return (
        <ComposedChart data={LATENCY_DATA}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} />
          <XAxis dataKey="time" tick={tick} />
          <YAxis tick={tick} unit="ms" />
          <Tooltip contentStyle={tooltip} />
          <Legend wrapperStyle={legend} />
          <Area type="monotone" dataKey="p99" name="p99" stroke="#ef4444" fill={t.isLight ? '#fef2f2' : 'rgba(239,68,68,0.15)'} strokeWidth={1.5} />
          <Area type="monotone" dataKey="p95" name="p95" stroke="#f59e0b" fill={t.isLight ? '#fffbeb' : 'rgba(245,158,11,0.15)'} strokeWidth={1.5} />
          <Line type="monotone" dataKey="p50" name="p50" stroke="#6366f1" strokeWidth={2} dot={false} />
        </ComposedChart>
      );
    }
    if (chartTab === 'errors') {
      return (
        <BarChart data={CHART_DATA}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} />
          <XAxis dataKey="time" tick={tick} />
          <YAxis tick={tick} />
          <Tooltip contentStyle={tooltip} />
          <Bar dataKey="failed" name="Failed executions" fill="#ef4444" radius={[2, 2, 0, 0]} />
        </BarChart>
      );
    }
    if (chartTab === 'cost') {
      return (
        <ComposedChart data={COST_DATA}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} />
          <XAxis dataKey="time" tick={tick} />
          <YAxis yAxisId="left" tick={tick} />
          <YAxis yAxisId="right" orientation="right" tick={tick} />
          <Tooltip contentStyle={tooltip} />
          <Legend wrapperStyle={legend} />
          <Bar yAxisId="left" dataKey="cost" name="Cost ($)" fill="#6366f1" radius={[2, 2, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="tokens" name="Tokens" stroke="#8b5cf6" strokeWidth={2} dot={false} />
        </ComposedChart>
      );
    }
    return (
      <ComposedChart data={COST_DATA}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis dataKey="time" tick={tick} />
        <YAxis tick={tick} />
        <Tooltip contentStyle={tooltip} />
        <Area type="monotone" dataKey="tokens" name="Total tokens" stroke="#8b5cf6" fill={t.isLight ? '#ede9fe' : 'rgba(139,92,246,0.2)'} strokeWidth={2} />
      </ComposedChart>
    );
  }, [chartTab, t]);

  return (
    <div className={`min-h-full -m-3 p-4 ${t.page}`}>
      {/* Header */}
      <div className="mb-4">
        <h1 className={`text-base font-semibold ${t.title}`}>AI Observability</h1>
        <p className={`text-[11px] ${t.subtitle} mt-0.5 max-w-3xl`}>
          Trace, debug, evaluate, and monitor every AI request across agents, workflows, LLM calls, knowledge retrieval, MCP tools, policies, and enterprise systems.
        </p>
      </div>

      {/* Toolbar */}
      <Card className="mb-4">
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
            <SelectField label="Environment" value={environment} onChange={(v) => setEnvironment(v as Environment)} options={['Production', 'Staging', 'Development']} />
            <SelectField label="Tenant" value={tenant} onChange={setTenant} options={['Acme Corp', 'Globex Inc', 'Initech Ltd']} />
            <SelectField label="Date Range" value={dateRange} onChange={(v) => setDateRange(v as DateRange)} options={['15m', '1h', '24h', '7d', '30d']} />
            <div className="col-span-2 flex flex-col gap-0.5">
              <label className={`text-[10px] font-medium ${t.muted}`}>Search</label>
              <div className="relative">
                <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${t.iconMuted}`} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Trace ID, user, agent, workflow, model, tool, policy..."
                  className={`w-full text-[11px] border rounded-md pl-7 pr-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${t.input}`}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {['Agent', 'Workflow', 'Model', 'Provider', 'Department', 'Status', 'Risk Level', 'Policy'].map((f) => (
              <select key={f} className={`text-[10px] border rounded-md px-2 py-1 ${t.select}`}>
                <option>{f}: All</option>
              </select>
            ))}
          </div>
          <div className={`flex flex-wrap gap-2 pt-1 border-t ${t.borderSubtle}`}>
            <button
              type="button"
              onClick={() => setLiveView((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${
                liveView ? 'bg-indigo-600 text-white border-indigo-600' : `${t.btnSecondary} ${t.surfaceHover}`
              }`}
            >
              <Radio className="w-3 h-3" />
              Live View
            </button>
            <button type="button" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${t.btnSecondary} ${t.surfaceHover}`}>
              <Download className="w-3 h-3" /> Export Traces
            </button>
            <button type="button" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${t.btnSecondary} ${t.surfaceHover}`}>
              <Bell className="w-3 h-3" /> Create Alert
            </button>
            <button type="button" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border ${t.btnSecondary} ${t.surfaceHover}`}>
              <GitCompare className="w-3 h-3" /> Compare Runs
            </button>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 mb-4">
        <KpiCard label="Total AI Requests" value="14,842" sub="Last 24 hours" trend={{ value: '12.4%', up: true }} accent="indigo" />
        <KpiCard label="Active Traces" value="28" sub="Running: 12" sub2="Queued: 16" accent="blue" />
        <KpiCard label="Success Rate" value="96.2%" sub="Failed: 562" accent="emerald" />
        <KpiCard label="Avg Latency" value="1.84s" sub="p50: 1.2s · p95: 4.8s" sub2="p99: 8.2s" accent="violet" />
        <KpiCard label="LLM Cost" value="$1,146" sub="Today" sub2="Monthly proj: $28.4K" accent="amber" />
        <KpiCard label="Prompt Firewall" value="56" sub="Blocked: 42" sub2="High-risk: 14" accent="red" />
        <KpiCard label="RAG Quality" value="91.4%" sub="Retrieval success" sub2="Avg similarity: 0.84" accent="emerald" />
        <KpiCard label="MCP Tool Health" value="2,418" sub="Tool calls" sub2="Failed: 38" accent="indigo" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        {/* Section 1: Traffic Chart */}
        <div className="col-span-12">
          <Card>
            <CardHeader title="AI Traffic & Latency Trends" subtitle="Volume, latency, errors, cost, and token consumption" />
            <TabBar tabs={CHART_TABS} active={chartTab} onChange={setChartTab} />
            <div className="p-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                {chartContent}
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Section 2: Live Trace Stream */}
        <div className="col-span-12 xl:col-span-7">
          <Card>
            <CardHeader
              title="Live Trace Stream"
              subtitle="Recent AI executions across agents, workflows, and tools"
              action={
                liveView && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                  </span>
                )
              }
            />
            <CompactTable
              stickyHeader
              headers={['Trace ID', 'User', 'Dept', 'Agent / Workflow', 'Intent', 'Status', 'Risk', 'Model', 'Cost', 'Duration', 'Policy', 'Started']}
            >
              {loading && liveView
                ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={12} />)
                : filteredTraces.length === 0
                  ? (
                    <tr>
                      <td colSpan={12}>
                        <EmptyState title="No traces found" description="Adjust filters or search criteria." />
                      </td>
                    </tr>
                  )
                  : filteredTraces.map((trace) => (
                    <tr
                      key={trace.id}
                      className={`cursor-pointer ${t.rowHover} ${selectedTrace?.id === trace.id ? t.rowSelected : ''}`}
                      onClick={() => selectTrace(trace)}
                    >
                      <td className={`px-3 py-2 text-[10px] font-mono ${t.accentText}`}>{trace.id.slice(0, 16)}…</td>
                      <td className={`px-3 py-2 text-[10px] ${t.textSecondary} max-w-[100px] truncate`}>{trace.user.split('@')[0]}</td>
                      <td className={`px-3 py-2 text-[10px] ${t.textSecondary}`}>{trace.department}</td>
                      <td className={`px-3 py-2 text-[10px] font-medium ${t.text}`}>{trace.agentWorkflow}</td>
                      <td className={`px-3 py-2 text-[10px] ${t.muted} max-w-[120px] truncate`}>{trace.intent}</td>
                      <td className="px-3 py-2"><StatusBadge status={trace.status} /></td>
                      <td className="px-3 py-2"><RiskBadge risk={trace.risk} /></td>
                      <td className={`px-3 py-2 text-[10px] ${t.text}`}>{trace.model}</td>
                      <td className={`px-3 py-2 text-[10px] font-mono ${t.text}`}>{trace.cost}</td>
                      <td className={`px-3 py-2 text-[10px] font-mono ${t.textSecondary}`}>{trace.duration}</td>
                      <td className={`px-3 py-2 text-[10px] ${t.muted} max-w-[90px] truncate`}>{trace.policy}</td>
                      <td className={`px-3 py-2 text-[10px] font-mono ${t.muted}`}>{trace.started}</td>
                    </tr>
                  ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 14: Alerts (sidebar column) */}
        <div className="col-span-12 xl:col-span-5">
          <Card className="h-full">
            <CardHeader title="Alerts & SLOs" subtitle="Active alerts and threshold violations" />
            <div className="p-3 space-y-2 max-h-[320px] overflow-y-auto">
              {ACTIVE_ALERTS.map((alert) => (
                <div key={alert.title} className={`flex gap-2 p-2 rounded-md border ${t.alertCard}`}>
                  <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
                    alert.severity === 'Critical' ? 'text-red-500' : alert.severity === 'High' ? 'text-amber-500' : 'text-blue-500'
                  }`} />
                  <div className="min-w-0">
                    <p className={`text-[11px] font-medium ${t.text}`}>{alert.title}</p>
                    <p className={`text-[10px] ${t.muted}`}>{alert.detail}</p>
                    <p className={`text-[10px] ${t.isLight ? 'text-slate-400' : 'text-zinc-600'} mt-0.5`}>{alert.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Section 3: Trace Inspection */}
        {selectedTrace && (
          <div className="col-span-12">
            <Card>
              <CardHeader title="Deep Trace Inspection" subtitle="Jaeger-style distributed trace view" />
              <div className={`px-4 py-3 border-b ${t.borderSubtle} ${t.surfaceMuted}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-[10px]">
                  <div><span className={t.muted}>Trace ID</span><p className={`font-mono font-medium ${t.accentText}`}>{selectedTrace.id}</p></div>
                  <div><span className={t.muted}>Agent</span><p className="font-medium">{selectedTrace.agent}</p></div>
                  <div><span className={t.muted}>Workflow</span><p className="font-medium">{selectedTrace.workflow}</p></div>
                  <div><span className={t.muted}>User</span><p className="font-medium truncate">{selectedTrace.user}</p></div>
                  <div><span className={t.muted}>Status</span><p><StatusBadge status={selectedTrace.status} /></p></div>
                  <div><span className={t.muted}>Risk Score</span><p className="font-medium text-amber-500">{selectedTrace.riskScore}/100</p></div>
                  <div><span className={t.muted}>Duration</span><p className="font-mono font-medium">{selectedTrace.duration}</p></div>
                  <div><span className={t.muted}>Cost</span><p className="font-mono font-medium">{selectedTrace.cost}</p></div>
                  <div><span className={t.muted}>Tokens</span><p className="font-mono font-medium">{selectedTrace.tokens.toLocaleString()}</p></div>
                  <div><span className={t.muted}>Policy</span><p className="font-medium">{selectedTrace.policy}</p></div>
                  <div><span className={t.muted}>Tenant</span><p className="font-medium">{selectedTrace.tenant}</p></div>
                  <div><span className={t.muted}>Department</span><p className="font-medium">{selectedTrace.department}</p></div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className={t.tableHead}>
                    <tr>
                      {['Span', 'Start', 'Duration', 'Status', 'Input', 'Output', 'Cost', 'Tokens', 'Retries', 'Policy / Error'].map((h) => (
                        <th key={h} className={`px-3 py-2 text-[10px] font-semibold uppercase ${t.tableHeadText} border-b ${t.border} whitespace-nowrap`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={t.tableDivide}>
                    <SpanTree
                      spans={TRACE_SPANS}
                      expanded={expandedSpans}
                      onToggle={toggleSpan}
                      selectedId={selectedSpan?.id ?? null}
                      onSelect={(s) => {
                        setSelectedSpan(s);
                        setDrawerOpen(true);
                      }}
                    />
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Section 4: LLM Inspector */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader
              title="LLM Call Inspector"
              subtitle="12 hops · Finance Copilot · tr-8f2a91c4"
            />
            <div className={`px-3 py-2 border-b ${t.borderSubtle} flex flex-wrap gap-3 text-[10px]`}>
              <span className={t.muted}>
                <span className={`font-semibold ${t.text}`}>2</span> LLM calls
              </span>
              <span className={t.muted}>
                <span className={`font-semibold ${t.text}`}>1</span> orchestration
              </span>
              <span className={t.muted}>
                <span className={`font-semibold ${t.text}`}>1</span> RAG
              </span>
              <span className={t.muted}>
                <span className={`font-semibold ${t.text}`}>1</span> MCP
              </span>
              <span className={`${t.muted} ml-auto`}>Click a row to simulate that call</span>
            </div>
            <LlmInspectorCallList
              calls={LLM_INSPECTOR_CALLS}
              onSelectCall={setSelectedCall}
            />
          </Card>
        </div>

        {/* Section 5: Prompt Firewall */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Prompt Firewall & Risk Inspection" subtitle="Classification, threats, and enforcement decisions" />
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><p className={`text-[10px] ${t.muted}`}>Classification</p><p className="text-[11px] font-medium">Financial Analysis</p></div>
                <div><p className={`text-[10px] ${t.muted}`}>Domain</p><p className="text-[11px] font-medium">Financial</p></div>
                <div><p className={`text-[10px] ${t.muted}`}>Risk Level</p><RiskBadge risk="Low" /></div>
              </div>
              <div>
                <p className={`text-[10px] font-semibold ${t.muted} uppercase mb-1.5`}>Detected Threats</p>
                <div className="flex flex-wrap gap-1">
                  {['Prompt Injection', 'Credential Extraction', 'Data Exfiltration'].map((threat) => (
                    <span key={threat} className={`px-1.5 py-0.5 text-[10px] border rounded ${t.threatDanger}`}>{threat}</span>
                  ))}
                  <span className={`px-1.5 py-0.5 text-[10px] border rounded ${t.threatSafe}`}>No active threats</span>
                </div>
              </div>
              <div className={`grid grid-cols-2 gap-3 p-3 rounded-md border ${t.surface} ${t.borderSubtle}`}>
                <div><p className={`text-[10px] ${t.muted}`}>Decision</p><span className="text-[11px] font-semibold text-emerald-500">Allow</span></div>
                <div><p className={`text-[10px] ${t.muted}`}>Confidence</p><span className="text-[11px] font-semibold">94.2%</span></div>
                <div className="col-span-2"><p className={`text-[10px] ${t.muted}`}>Matched Rule</p><p className="text-[11px]">fin-analysis-allow-v2</p></div>
                <div className="col-span-2"><p className={`text-[10px] ${t.muted}`}>Reason</p><p className={`text-[11px] ${t.textSecondary}`}>Standard financial query within authorized domain. No injection patterns detected.</p></div>
              </div>
            </div>
          </Card>
        </div>

        {/* Section 6: RAG Inspector */}
        <div className="col-span-12">
          <Card>
            <CardHeader title="RAG / Knowledge Retrieval Inspector" subtitle="Finance KB · Pinecone · text-embedding-3-large" />
            <div className={`px-4 py-3 border-b ${t.borderSubtle} grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-[10px]`}>
              <FieldRow label="Knowledge Base" value="Finance KB" />
              <FieldRow label="Vector Store" value="Pinecone (prod)" />
              <FieldRow label="Embedding Model" value="text-embedding-3-large" />
              <FieldRow label="Query Rewrite" value="Enabled" />
              <FieldRow label="Retrieval Latency" value="486ms" />
              <FieldRow label="Policy Filtered" value="2 docs" />
            </div>
            <CompactTable headers={['Rank', 'Document', 'Chunk ID', 'Similarity', 'Rerank', 'Policy', 'Used', 'Citation']}>
              {RAG_CHUNKS.map((c) => (
                <tr key={c.chunkId} className={t.rowHover}>
                  <td className="px-3 py-2 text-[10px] font-mono">{c.rank}</td>
                  <td className={`px-3 py-2 text-[10px] font-medium ${t.text}`}>{c.document}</td>
                  <td className={`px-3 py-2 text-[10px] font-mono ${t.muted}`}>{c.chunkId}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{(c.similarity * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{(c.rerankScore * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2 text-[10px]">{c.policyStatus}</td>
                  <td className="px-3 py-2 text-[10px]">{c.usedInAnswer ? '✓' : '—'}</td>
                  <td className={`px-3 py-2 text-[10px] font-mono ${t.accentText}`}>{c.citation}</td>
                </tr>
              ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 7: MCP Inspector */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="MCP Tool & Connector Inspector" subtitle="CustomerDB.Read · MCP Server prod-us-east" />
            <div className="p-4 space-y-1">
              <FieldRow label="MCP Server" value="customer-db-prod" mono />
              <FieldRow label="Tool" value="CustomerDB.Read" />
              <FieldRow label="Version" value="2.1.4" />
              <FieldRow label="Execution Time" value="186ms" />
              <FieldRow label="Retry Count" value="0" />
              <FieldRow label="Rate Limit" value="OK (842/1000 rpm)" />
              <FieldRow label="Authorization" value="Allowed" />
              <FieldRow label="Policy Decision" value="Allowed — Financial Strict" />
              <FieldRow label="Records Accessed" value="3" />
              <FieldRow label="Rows Returned" value="3" />
              <FieldRow label="Downstream Latency" value="142ms" />
              <div className={`mt-3 p-2 rounded border ${t.surface} ${t.borderSubtle}`}>
                <p className={`text-[10px] font-semibold ${t.muted} mb-1`}>Input Parameters</p>
                <pre className={`text-[10px] font-mono ${t.textSecondary}`}>{`{ "customer_id": "C-88421", "fields": ["revenue", "segment"] }`}</pre>
              </div>
            </div>
          </Card>
        </div>

        {/* Section 8: Workflow Nodes */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Workflow Node Observability" subtitle="Credit Risk Workflow execution graph" />
            <div className="p-4 space-y-2">
              {WORKFLOW_NODES.map((node, i) => {
                const isExpanded = expandedNodes.has(node.id);
                return (
                  <div key={node.id} className={`border rounded-md overflow-hidden ${t.nodeCard}`}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedNodes((prev) => {
                          const next = new Set(prev);
                          if (next.has(node.id)) next.delete(node.id);
                          else next.add(node.id);
                          return next;
                        })
                      }
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left ${t.nodeHeader}`}
                    >
                      <span className={`text-[10px] font-mono ${t.iconMuted} w-4`}>{i + 1}</span>
                      <SpanStatusDot status={node.status} />
                      <span className={`text-[11px] font-medium ${t.text} flex-1`}>{node.name}</span>
                      <span className={`text-[10px] ${t.muted}`}>{node.type}</span>
                      <span className={`text-[10px] font-mono ${t.textSecondary}`}>{node.duration}</span>
                      {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </button>
                    {isExpanded && (
                      <div className={`px-3 py-2 space-y-1 border-t ${t.borderSubtle} text-[10px]`}>
                        <FieldRow label="Input" value={node.inputPreview} />
                        <FieldRow label="Output" value={node.outputPreview} />
                        <FieldRow label="Cost" value={node.cost} />
                        {node.policyDecision && <FieldRow label="Policy" value={node.policyDecision} />}
                        {node.error && <FieldRow label="Error" value={node.error} />}
                        <div className="flex gap-1 pt-1">
                          {['View input', 'View output', 'View logs', 'Replay', 'Retry'].map((a) => (
                            <button key={a} type="button" className={`px-2 py-0.5 text-[10px] border rounded ${t.btnSecondary} ${t.surfaceHover}`}>{a}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Section 9: Governance */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Governance & Policy Observability" subtitle="Financial Strict v2.4.1 · Department Default" />
            <div className="p-4 grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <SectionLabel>Decisions</SectionLabel>
                {['Prompt allowed', 'KB allowed', 'MCP tool allowed', 'Model allowed', 'Output masked'].map((d) => (
                  <div key={d} className="flex items-center gap-1.5 text-[10px] text-emerald-700">
                    <Shield className="w-3 h-3" /> {d}
                  </div>
                ))}
              </div>
              <div className="space-y-1">
                <SectionLabel>Violations</SectionLabel>
                <p className={`text-[10px] ${t.isLight ? 'text-slate-400' : 'text-zinc-600'}`}>No violations in this trace</p>
              </div>
              <div className={`col-span-2 space-y-1 border-t ${t.borderSubtle} pt-3`}>
                <FieldRow label="Audit Status" value="Logged · Retained · SOC 2 mapped" />
                <FieldRow label="Compliance" value="SOX, PCI-DSS" />
              </div>
            </div>
          </Card>
        </div>

        {/* Section 10: Cost Analytics */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Cost & Token Analytics" subtitle="Spend breakdown and forecasts" />
            <div className="p-4 grid grid-cols-2 gap-4">
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={COST_BY_MODEL} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={30} outerRadius={55} paddingAngle={2}>
                      {COST_BY_MODEL.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={t.chartTooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1 text-[10px]">
                <FieldRow label="Avg cost / request" value="$0.077" />
                <FieldRow label="Monthly forecast" value="$28,420" />
                <FieldRow label="Cost anomaly" value="+34% vs forecast" />
              </div>
            </div>
            <CompactTable headers={['Resource', 'Type', 'Provider', 'Tokens', 'Cost', 'Latency', 'Success', 'Trend']}>
              {COST_ROWS.map((r) => (
                <tr key={r.resource} className={t.rowHover}>
                  <td className="px-3 py-2 text-[10px] font-medium">{r.resource}</td>
                  <td className="px-3 py-2 text-[10px]">{r.type}</td>
                  <td className="px-3 py-2"><ProviderBadge provider={r.provider} /></td>
                  <td className="px-3 py-2 text-[10px] font-mono">{r.tokens}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{r.cost}</td>
                  <td className="px-3 py-2 text-[10px]">{r.avgLatency}</td>
                  <td className="px-3 py-2 text-[10px]">{r.successRate}</td>
                  <td className="px-3 py-2 text-[10px]">{r.trend === 'up' ? '↑' : r.trend === 'down' ? '↓' : '—'}</td>
                </tr>
              ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 11: Errors */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Errors, Retries & Failures" subtitle="Error rate: 3.8% · 562 failures in 24h" />
            <div className={`px-4 py-2 grid grid-cols-3 gap-2 border-b ${t.borderSubtle}`}>
              {[
                { label: 'Failed LLM', value: '186' },
                { label: 'Failed MCP', value: '38' },
                { label: 'Policy Blocks', value: '42' },
                { label: 'Timeouts', value: '24' },
                { label: 'Rate Limits', value: '18' },
                { label: 'Auth Failures', value: '6' },
              ].map((s) => (
                <div key={s.label} className={`p-2 rounded border ${t.statBox}`}>
                  <p className={`text-[10px] ${t.muted}`}>{s.label}</p>
                  <p className={`text-sm font-semibold ${t.text}`}>{s.value}</p>
                </div>
              ))}
            </div>
            <CompactTable headers={['Error Type', 'Count', 'Resource', 'Last Seen', 'Severity', 'Fix', '']}>
              {ERROR_ROWS.map((e) => (
                <tr key={e.errorType} className={t.rowHover}>
                  <td className="px-3 py-2 text-[10px] font-medium">{e.errorType}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{e.count}</td>
                  <td className="px-3 py-2 text-[10px]">{e.resource}</td>
                  <td className={`px-3 py-2 text-[10px] ${t.muted}`}>{e.lastSeen}</td>
                  <td className="px-3 py-2"><RiskBadge risk={e.severity} /></td>
                  <td className={`px-3 py-2 text-[10px] ${t.muted} max-w-[100px] truncate`}>{e.suggestedFix}</td>
                  <td className="px-3 py-2">
                    <button type="button" className={`text-[10px] ${t.link} hover:underline`}>View traces</button>
                  </td>
                </tr>
              ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 12: Evaluation */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Evaluation & Quality Monitoring" subtitle="Automated and human review scores" />
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              {EVALUATION_SCORES.map((e) => (
                <div key={e.label} className={`p-3 border rounded-md text-center ${t.borderSubtle}`}>
                  <p className={`text-[10px] ${t.muted}`}>{e.label}</p>
                  <p className="text-lg font-semibold tabular-nums" style={{ color: e.color }}>{e.score}</p>
                </div>
              ))}
            </div>
            <div className="px-4 pb-4 grid grid-cols-2 gap-3 text-[10px]">
              <FieldRow label="Groundedness" value="89%" />
              <FieldRow label="Citation coverage" value="92%" />
              <FieldRow label="Hallucination risk" value="Low (8%)" />
              <FieldRow label="User feedback" value="4.2 / 5.0" />
            </div>
          </Card>
        </div>

        {/* Section 13: Department Analytics */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="User & Department Analytics" subtitle="Usage patterns and risk distribution" />
            <CompactTable headers={['Department', 'Requests', 'Cost', 'Blocked', 'High-Risk']}>
              {DEPT_ANALYTICS.map((d) => (
                <tr key={d.dept} className={t.rowHover}>
                  <td className="px-3 py-2 text-[10px] font-medium">{d.dept}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{d.requests.toLocaleString()}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{d.cost}</td>
                  <td className="px-3 py-2 text-[10px]">{d.blocked}</td>
                  <td className="px-3 py-2 text-[10px]">{d.risk}</td>
                </tr>
              ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 14: Alert Rules */}
        <div className="col-span-12 lg:col-span-6">
          <Card>
            <CardHeader title="Alert Rules" subtitle="Configure metric thresholds and notifications" action={
              <button type="button" className={`text-[10px] font-medium ${t.link} hover:underline`}>+ New Rule</button>
            } />
            <CompactTable headers={['Metric', 'Condition', 'Threshold', 'Window', 'Severity', 'Channel', 'Active']}>
              {ALERT_RULES.map((r) => (
                <tr key={r.id} className={t.rowHover}>
                  <td className="px-3 py-2 text-[10px] font-medium">{r.metric}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{r.condition}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{r.threshold}</td>
                  <td className="px-3 py-2 text-[10px]">{r.window}</td>
                  <td className="px-3 py-2"><RiskBadge risk={r.severity} /></td>
                  <td className="px-3 py-2 text-[10px]">{r.channel}</td>
                  <td className="px-3 py-2">
                    <span className={`w-2 h-2 rounded-full inline-block ${r.active ? 'bg-emerald-500' : t.isLight ? 'bg-slate-300' : 'bg-zinc-600'}`} />
                  </td>
                </tr>
              ))}
            </CompactTable>
          </Card>
        </div>

        {/* Section 15: Debug Tools */}
        <div className="col-span-12">
          <Card>
            <CardHeader title="Trace Replay & Debug Tools" subtitle="Investigation and remediation actions" />
            <div className="p-4 flex flex-wrap gap-2">
              {[
                { icon: Play, label: 'Replay trace' },
                { icon: RotateCcw, label: 'Replay from failed node' },
                { icon: GitCompare, label: 'Compare with previous run' },
                { icon: Download, label: 'Export trace JSON' },
                { icon: Copy, label: 'View raw payload' },
                { icon: Shield, label: 'View policy evaluation' },
                { icon: Zap, label: 'View prompt version' },
                { icon: RefreshCw, label: 'View model configuration' },
                { icon: ExternalLink, label: 'Open workflow run' },
                { icon: ExternalLink, label: 'Open audit log' },
              ].map(({ icon: Icon, label }) => (
                <button
                  key={label}
                  type="button"
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border rounded-md ${t.btnSecondary} ${t.surfaceHover}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${t.link}`} />
                  {label}
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {drawerOpen && selectedTrace && (
        <TraceDrawer trace={selectedTrace} selectedSpan={selectedSpan} onClose={() => setDrawerOpen(false)} />
      )}

      <LlmFlowSimulationModal call={selectedCall} onClose={() => setSelectedCall(null)} />
    </div>
  );
}
